/**
 * CategoryLoader - Singleton service for loading and managing prompt categories
 *
 * This service provides:
 * - JSON file reading and parsing
 * - Category data validation
 * - In-memory caching with cache invalidation
 * - Singleton pattern for global access
 * - Comprehensive error handling with structured logging
 * - Graceful fallback mechanisms for non-critical errors
 *
 * Logging is handled by UniversalLogger (database namespace) for consistency
 * across the application.
 *
 * Usage:
 * const loader = CategoryLoader.getInstance();
 * const categories = loader.getCategories();
 */

import { Category, CategoryValidationResult, SingleCategoryValidationResult, FieldValidationResult } from '@/types/categories';
import { QuickLoggerFactory, INamespaceLogger } from '@/lib/universal-logger';

// Import the categories data directly for browser environment
import categoriesData from '@/data/categories.json';

// Use the database namespace from UniversalLogger for all category operations
const logger = QuickLoggerFactory.database();

/**
 * Logger configuration and levels
 * @deprecated Use UniversalLogger log levels instead. Kept for backward compatibility.
 */
export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3
}

/**
 * @deprecated Use UniversalLogger log entries instead. Kept for backward compatibility.
 */
export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: string;
  data?: unknown;
  error?: Error;
}

/**
 * CategoryLogger adapter that wraps UniversalLogger for backward compatibility.
 * New code should use QuickLoggerFactory.database() directly.
 *
 * @deprecated Use QuickLoggerFactory.database() directly instead.
 */
export class CategoryLogger {
  private static instance: CategoryLogger | null = null;
  private logger: INamespaceLogger;
  private logs: LogEntry[] = [];
  private maxLogEntries: number = 1000;

  private constructor() {
    this.logger = QuickLoggerFactory.database();
  }

  public static getInstance(): CategoryLogger {
    if (!CategoryLogger.instance) {
      CategoryLogger.instance = new CategoryLogger();
    }
    return CategoryLogger.instance;
  }

  public error(message: string, context?: string, data?: unknown, error?: Error): void {
    const logContext = { context, ...this.normalizeData(data) };
    this.logger.error(message, logContext, error);
    this.addToInternalLogs(LogLevel.ERROR, message, context, data, error);
  }

  public warn(message: string, context?: string, data?: unknown): void {
    const logContext = { context, ...this.normalizeData(data) };
    this.logger.warn(message, logContext);
    this.addToInternalLogs(LogLevel.WARN, message, context, data);
  }

  public info(message: string, context?: string, data?: unknown): void {
    const logContext = { context, ...this.normalizeData(data) };
    this.logger.info(message, logContext);
    this.addToInternalLogs(LogLevel.INFO, message, context, data);
  }

  public debug(message: string, context?: string, data?: unknown): void {
    const logContext = { context, ...this.normalizeData(data) };
    this.logger.debug(message, logContext);
    this.addToInternalLogs(LogLevel.DEBUG, message, context, data);
  }

  private normalizeData(data: unknown): Record<string, unknown> {
    if (data === null || data === undefined) {
      return {};
    }
    if (typeof data === 'object' && !Array.isArray(data)) {
      return data as Record<string, unknown>;
    }
    return { data };
  }

  private addToInternalLogs(level: LogLevel, message: string, context?: string, data?: unknown, error?: Error): void {
    this.logs.push({
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      data,
      error
    });

    // Trim logs if exceeding max entries
    if (this.logs.length > this.maxLogEntries) {
      this.logs = this.logs.slice(-this.maxLogEntries);
    }
  }

  public getLogs(level?: LogLevel, limit?: number): LogEntry[] {
    const filteredLogs = level !== undefined ? this.logs.filter(log => log.level <= level) : this.logs;
    return limit ? filteredLogs.slice(-limit) : filteredLogs;
  }

  public clearLogs(): void {
    this.logs = [];
  }

  public setLogLevel(_level: LogLevel): void {
    // Log level is now managed by UniversalLogger configuration
    this.info('Log level management delegated to UniversalLogger', 'Logger');
  }

  public getLogLevel(): LogLevel {
    // Return INFO as default - actual level is managed by UniversalLogger
    return LogLevel.INFO;
  }
}

/**
 * Custom error classes for different failure scenarios
 */
export class CategoryLoaderError extends Error {
  constructor(message: string, public readonly code: string, public readonly context?: string) {
    super(message);
    this.name = 'CategoryLoaderError';
  }
}

export class CategoryValidationError extends CategoryLoaderError {
  constructor(message: string, public readonly validationErrors: string[], context?: string) {
    super(message, 'VALIDATION_ERROR', context);
    this.name = 'CategoryValidationError';
  }
}

export class CategoryFileError extends CategoryLoaderError {
  constructor(message: string, context?: string) {
    super(message, 'FILE_ERROR', context);
    this.name = 'CategoryFileError';
  }
}

export class CategoryFallbackError extends CategoryLoaderError {
  constructor(message: string, context?: string) {
    super(message, 'FALLBACK_ERROR', context);
    this.name = 'CategoryFallbackError';
  }
}

/**
 * Recovery strategies for different error scenarios
 */
export enum RecoveryStrategy {
  RETRY = 'retry',
  FALLBACK = 'fallback',
  FAIL = 'fail'
}

export interface ErrorContext {
  operation: string;
  attempts: number;
  maxAttempts: number;
  strategy: RecoveryStrategy;
  lastError?: Error;
}

/**
 * Fallback category data for critical error scenarios
 */
const FALLBACK_CATEGORIES: Category[] = [
  { id: 1, name: 'Programming', description: 'Code-related prompts and technical discussions' },
  { id: 2, name: 'Writing', description: 'Creative writing, editing, and content creation' },
  { id: 3, name: 'Analysis', description: 'Data analysis, research, and analytical tasks' },
  { id: 15, name: 'Other', description: 'General or uncategorized prompts' }
];

/**
 * CategoryLoader singleton class for managing prompt categories
 */
export class CategoryLoader {
  private static instance: CategoryLoader | null = null;
  private categories: Category[] = [];
  private isLoaded: boolean = false;
  private lastLoadTime: number = 0;
  private readonly cacheTTL: number = 5 * 60 * 1000; // 5 minutes cache
  private loadCount: number = 0;
  private lastAccessTime: number = 0;
  private cacheHits: number = 0;
  private errorCount: number = 0;
  private fallbackUsed: boolean = false;
  private lastError: Error | null = null;
  private logger: CategoryLogger;
  private cacheStats: {
    totalRequests: number;
    cacheHits: number;
    cacheMisses: number;
    totalLoadTime: number;
    averageLoadTime: number;
  } = {
    totalRequests: 0,
    cacheHits: 0,
    cacheMisses: 0,
    totalLoadTime: 0,
    averageLoadTime: 0
  };

  /**
   * Private constructor to enforce singleton pattern
   */
  private constructor() {
    this.logger = CategoryLogger.getInstance();
    this.logger.info('Initializing CategoryLoader singleton', 'Constructor');
    this.loadCategories();
  }

  /**
   * Get the singleton instance of CategoryLoader
   * @returns The CategoryLoader instance
   */
  public static getInstance(): CategoryLoader {
    if (!CategoryLoader.instance) {
      CategoryLoader.instance = new CategoryLoader();
    }
    return CategoryLoader.instance;
  }

  /**
   * Reset the singleton instance (useful for testing)
   */
  public static resetInstance(): void {
    const logger = CategoryLogger.getInstance();
    logger.info('Resetting CategoryLoader singleton instance', 'Reset');
    CategoryLoader.instance = null;
  }

  /**
   * Execute operation with retry logic and fallback mechanisms
   */
  private executeWithRetry<T>(
    operation: () => T,
    context: ErrorContext,
    fallbackFn?: () => T
  ): T {
    this.logger.debug(`Executing operation: ${context.operation}`, 'Retry', { 
      attempt: context.attempts, 
      maxAttempts: context.maxAttempts 
    });

    try {
      const result = operation();
      if (context.attempts > 1) {
        this.logger.info(`Operation succeeded after ${context.attempts} attempts`, context.operation);
      }
      return result;
    } catch (error) {
      context.lastError = error instanceof Error ? error : new Error(String(error));
      context.attempts++;

      this.logger.warn(
        `Operation failed (attempt ${context.attempts}/${context.maxAttempts})`,
        context.operation,
        { error: context.lastError.message }
      );

      if (context.attempts < context.maxAttempts && context.strategy === RecoveryStrategy.RETRY) {
        // Exponential backoff delay for retries
        const delay = Math.min(1000 * Math.pow(2, context.attempts - 1), 5000);
        this.logger.debug(`Retrying in ${delay}ms`, context.operation);
        // In a real environment, you might want to implement actual delay
        return this.executeWithRetry(operation, context, fallbackFn);
      }

      if (context.strategy === RecoveryStrategy.FALLBACK && fallbackFn) {
        this.logger.warn(`Using fallback mechanism for ${context.operation}`, 'Fallback');
        try {
          return fallbackFn();
        } catch (fallbackError) {
          this.logger.error(
            `Fallback mechanism failed for ${context.operation}`,
            'Fallback',
            { originalError: context.lastError.message, fallbackError: fallbackError instanceof Error ? fallbackError.message : String(fallbackError) },
            fallbackError instanceof Error ? fallbackError : undefined
          );
          throw fallbackError;
        }
      }

      this.errorCount++;
      this.lastError = context.lastError;
      throw context.lastError;
    }
  }

  /**
   * Load categories from the JSON data
   * This handles both initial loading and cache refresh
   */
  private loadCategories(): void {
    const context: ErrorContext = {
      operation: 'loadCategories',
      attempts: 0,
      maxAttempts: 3,
      strategy: RecoveryStrategy.FALLBACK
    };

    const loadOperation = () => {
      const currentTime = Date.now();
      const loadStartTime = performance.now();
      
      this.logger.debug('Starting category load operation', 'LoadCategories', {
        isLoaded: this.isLoaded,
        cacheAge: this.isLoaded ? currentTime - this.lastLoadTime : 0,
        cacheTTL: this.cacheTTL
      });

      // Update access time and request count
      this.lastAccessTime = currentTime;
      this.cacheStats.totalRequests++;
      
      // Check if we need to reload (cache expired or first load)
      if (this.isLoaded && (currentTime - this.lastLoadTime) < this.cacheTTL) {
        this.cacheHits++;
        this.cacheStats.cacheHits++;
        this.logger.debug('Using cached categories', 'LoadCategories', { 
          categoryCount: this.categories.length 
        });
        return;
      }

      // Cache miss - need to load data
      this.cacheStats.cacheMisses++;
      this.loadCount++;

      this.logger.info('Loading categories from data source', 'LoadCategories', {
        loadCount: this.loadCount,
        cacheMiss: true
      });

      // Parse and validate the categories data
      const parsedCategories = this.parseCategories(categoriesData);
      const validationResult = this.validateCategories(parsedCategories);
      
      if (!validationResult.isValid) {
        this.logger.error(
          'Category data validation failed',
          'Validation',
          { 
            errorCount: validationResult.errors.length,
            errors: validationResult.errors.slice(0, 5), // Limit log size
            validCategories: validationResult.summary.validCategories,
            totalCategories: validationResult.summary.totalCategories
          }
        );
        
        throw new CategoryValidationError(
          'Category data validation failed',
          validationResult.errors,
          'LoadCategories'
        );
      }

      // Store validated categories and update cache timestamp
      this.categories = parsedCategories;
      this.isLoaded = true;
      this.lastLoadTime = currentTime;
      this.fallbackUsed = false;

      // Update load time statistics
      const loadDuration = performance.now() - loadStartTime;
      this.cacheStats.totalLoadTime += loadDuration;
      this.cacheStats.averageLoadTime = this.cacheStats.totalLoadTime / this.loadCount;

      // Log successful load
      this.logger.info(
        `Successfully loaded ${this.categories.length} categories`,
        'LoadCategories',
        {
          categoryCount: this.categories.length,
          loadDuration: Math.round(loadDuration * 100) / 100,
          warningCount: validationResult.warnings.length
        }
      );
      
      if (validationResult.warnings.length > 0) {
        this.logger.warn(
          'Category data has warnings',
          'Validation',
          { warnings: validationResult.warnings }
        );
      }
    };

    const fallbackOperation = () => {
      this.logger.warn('Loading fallback categories due to error', 'Fallback');
      this.categories = [...FALLBACK_CATEGORIES];
      this.isLoaded = true;
      this.lastLoadTime = Date.now();
      this.fallbackUsed = true;
      
      this.logger.info(
        `Loaded ${this.categories.length} fallback categories`,
        'Fallback',
        { fallbackUsed: true }
      );
    };

    try {
      this.executeWithRetry(loadOperation, context, fallbackOperation);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      this.logger.error(
        'All attempts to load categories failed, including fallback',
        'LoadCategories',
        { error: errorMessage },
        error instanceof Error ? error : undefined
      );
      
      if (error instanceof CategoryLoaderError) {
        throw error;
      }
      
      throw new CategoryFileError(`Failed to load categories: ${errorMessage}`, 'LoadCategories');
    }
  }

  /**
   * Parse raw JSON data into Category objects
   * @param rawData The raw JSON data
   * @returns Array of Category objects
   */
  private parseCategories(rawData: any): Category[] {
    this.logger.debug('Starting category parsing', 'ParseCategories', {
      dataType: typeof rawData,
      isArray: Array.isArray(rawData),
      length: Array.isArray(rawData) ? rawData.length : 'N/A'
    });

    if (!Array.isArray(rawData)) {
      const error = 'Categories data must be an array';
      this.logger.error(error, 'ParseCategories', { 
        actualType: typeof rawData,
        expectedType: 'array'
      });
      throw new CategoryFileError(error, 'ParseCategories');
    }

    if (rawData.length === 0) {
      this.logger.warn('Empty categories array provided', 'ParseCategories');
      return [];
    }

    this.logger.debug(`Parsing ${rawData.length} category items`, 'ParseCategories');

    try {
      const categories = rawData.map((item, index) => {
        if (typeof item !== 'object' || item === null) {
          const error = `Invalid category object at index ${index}`;
          this.logger.error(error, 'ParseCategories', {
            index,
            itemType: typeof item,
            itemValue: item
          });
          throw new CategoryFileError(error, 'ParseCategories');
        }

        const category: Category = {
          id: item.id,
          name: item.name,
          description: item.description
        };

        // Log parsing of each category at debug level
        this.logger.debug(`Parsed category ${index}`, 'ParseCategories', {
          id: category.id,
          name: category.name,
          hasDescription: !!category.description
        });

        return category;
      });

      this.logger.info(`Successfully parsed ${categories.length} categories`, 'ParseCategories');
      return categories;
    } catch (error) {
      this.logger.error(
        'Failed to parse categories data',
        'ParseCategories',
        { error: error instanceof Error ? error.message : String(error) },
        error instanceof Error ? error : undefined
      );
      throw error;
    }
  }

  /**
   * Validate category data structure and business rules
   * @param categories Array of categories to validate
   * @returns Enhanced validation result with detailed error information
   */
  private validateCategories(categories: Category[]): CategoryValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const fieldErrors: { [field: string]: string[] } = {};
    const businessRuleViolations: string[] = [];
    const seenIds = new Set<number>();
    const seenNames = new Set<string>();
    let validCategories = 0;

    // Helper function to add field error
    const addFieldError = (field: string, error: string) => {
      if (!fieldErrors[field]) {
        fieldErrors[field] = [];
      }
      fieldErrors[field].push(error);
      errors.push(error);
    };

    if (categories.length === 0) {
      const error = 'Categories array cannot be empty';
      addFieldError('array', error);
      return {
        isValid: false,
        errors,
        warnings,
        fieldErrors,
        businessRuleViolations,
        summary: {
          totalCategories: 0,
          validCategories: 0,
          errorCount: errors.length,
          warningCount: warnings.length
        }
      };
    }

    categories.forEach((category, index) => {
      const context = `Category at index ${index}`;
      let categoryIsValid = true;

      // Validate required fields
      const idValidation = this.validateCategoryId(category.id, index);
      if (!idValidation.isValid) {
        categoryIsValid = false;
        idValidation.errors.forEach(error => addFieldError('id', `${context}: ${error}`));
      }

      // Check for duplicate IDs (business rule)
      if (typeof category.id === 'number' && category.id > 0) {
        if (seenIds.has(category.id)) {
          categoryIsValid = false;
          const error = `Duplicate id ${category.id}`;
          businessRuleViolations.push(`${context}: ${error}`);
          errors.push(`${context}: ${error}`);
        } else {
          seenIds.add(category.id);
        }
      }

      const nameValidation = this.validateCategoryName(category.name, index);
      if (!nameValidation.isValid) {
        categoryIsValid = false;
        nameValidation.errors.forEach(error => addFieldError('name', `${context}: ${error}`));
      }

      // Check for duplicate names (business rule)
      if (typeof category.name === 'string' && category.name.trim().length > 0) {
        if (seenNames.has(category.name)) {
          categoryIsValid = false;
          const error = `Duplicate name '${category.name}'`;
          businessRuleViolations.push(`${context}: ${error}`);
          errors.push(`${context}: ${error}`);
        } else {
          seenNames.add(category.name);
        }
      }

      // Validate optional fields
      const descriptionValidation = this.validateCategoryDescription(category.description, index);
      if (!descriptionValidation.isValid) {
        categoryIsValid = false;
        descriptionValidation.errors.forEach(error => addFieldError('description', `${context}: ${error}`));
      }

      // Business rule warnings
      if (category.name && category.name !== category.name.trim()) {
        warnings.push(`${context}: Category name has leading/trailing whitespace`);
      }

      if (categoryIsValid) {
        validCategories++;
      }
    });

    // Check for required default category (business rule)
    if (!categories.some(cat => cat.name === 'Other')) {
      const warning = 'No "Other" category found - this is recommended as a fallback';
      warnings.push(warning);
      businessRuleViolations.push(warning);
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      fieldErrors,
      businessRuleViolations,
      summary: {
        totalCategories: categories.length,
        validCategories,
        errorCount: errors.length,
        warningCount: warnings.length
      }
    };
  }

  /**
   * Validate individual category ID field
   * @param id The category ID to validate
   * @param index Category index for error context
   * @returns Field validation result
   */
  private validateCategoryId(id: any, index: number): FieldValidationResult {
    const errors: string[] = [];

    if (typeof id !== 'number') {
      errors.push("'id' must be a number");
    } else if (!Number.isInteger(id)) {
      errors.push("'id' must be an integer");
    } else if (id <= 0) {
      errors.push("'id' must be a positive number");
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate individual category name field
   * @param name The category name to validate
   * @param index Category index for error context
   * @returns Field validation result
   */
  private validateCategoryName(name: any, index: number): FieldValidationResult {
    const errors: string[] = [];

    if (typeof name !== 'string') {
      errors.push("'name' must be a string");
    } else if (name.trim().length === 0) {
      errors.push("'name' cannot be empty");
    } else if (name.length > 50) {
      errors.push("'name' cannot exceed 50 characters");
    } else if (name.includes('\n') || name.includes('\r')) {
      errors.push("'name' cannot contain line breaks");
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate individual category description field
   * @param description The category description to validate
   * @param index Category index for error context
   * @returns Field validation result
   */
  private validateCategoryDescription(description: any, index: number): FieldValidationResult {
    const errors: string[] = [];

    if (description !== undefined) {
      if (typeof description !== 'string') {
        errors.push("'description' must be a string if provided");
      } else if (description.length > 200) {
        errors.push("'description' cannot exceed 200 characters");
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate a single category object
   * @param category The category to validate
   * @param index Category index for error context
   * @returns Single category validation result
   */
  public validateSingleCategory(category: any, index: number = 0): SingleCategoryValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const fieldErrors: { [field: string]: string[] } = {};

    // Helper function to add field error
    const addFieldError = (field: string, error: string) => {
      if (!fieldErrors[field]) {
        fieldErrors[field] = [];
      }
      fieldErrors[field].push(error);
      errors.push(error);
    };

    // Validate that it's an object
    if (typeof category !== 'object' || category === null) {
      addFieldError('object', 'Category must be a non-null object');
      return { isValid: false, errors, warnings, fieldErrors };
    }

    // Validate individual fields
    const idValidation = this.validateCategoryId(category.id, index);
    if (!idValidation.isValid) {
      idValidation.errors.forEach(error => addFieldError('id', error));
    }

    const nameValidation = this.validateCategoryName(category.name, index);
    if (!nameValidation.isValid) {
      nameValidation.errors.forEach(error => addFieldError('name', error));
    }

    const descriptionValidation = this.validateCategoryDescription(category.description, index);
    if (!descriptionValidation.isValid) {
      descriptionValidation.errors.forEach(error => addFieldError('description', error));
    }

    // Check for unexpected properties
    const expectedProperties = ['id', 'name', 'description'];
    const actualProperties = Object.keys(category);
    const unexpectedProperties = actualProperties.filter(prop => !expectedProperties.includes(prop));
    
    if (unexpectedProperties.length > 0) {
      warnings.push(`Unexpected properties found: ${unexpectedProperties.join(', ')}`);
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      fieldErrors
    };
  }

  /**
   * Get all categories
   * @returns Array of all categories (defensive copy)
   */
  public getCategories(): Category[] {
    try {
      this.ensureLoaded();
      this.logger.debug('Retrieved all categories', 'GetCategories', {
        categoryCount: this.categories.length,
        fallbackUsed: this.fallbackUsed
      });
      return [...this.categories];
    } catch (error) {
      this.logger.error(
        'Failed to get categories',
        'GetCategories',
        { error: error instanceof Error ? error.message : String(error) },
        error instanceof Error ? error : undefined
      );
      throw error;
    }
  }

  /**
   * Get a category by its ID
   * @param id The category ID to find
   * @returns The category if found, undefined otherwise
   */
  public getCategoryById(id: number): Category | undefined {
    try {
      this.ensureLoaded();
      const category = this.categories.find(category => category.id === id);
      
      this.logger.debug('Category lookup by ID', 'GetCategoryById', {
        searchId: id,
        found: !!category,
        categoryName: category?.name
      });
      
      return category;
    } catch (error) {
      this.logger.error(
        'Failed to get category by ID',
        'GetCategoryById',
        { id, error: error instanceof Error ? error.message : String(error) },
        error instanceof Error ? error : undefined
      );
      throw error;
    }
  }

  /**
   * Get a category by its name
   * @param name The category name to find (case-sensitive)
   * @returns The category if found, undefined otherwise
   */
  public getCategoryByName(name: string): Category | undefined {
    try {
      this.ensureLoaded();
      const category = this.categories.find(category => category.name === name);
      
      this.logger.debug('Category lookup by name', 'GetCategoryByName', {
        searchName: name,
        found: !!category,
        categoryId: category?.id
      });
      
      return category;
    } catch (error) {
      this.logger.error(
        'Failed to get category by name',
        'GetCategoryByName',
        { name, error: error instanceof Error ? error.message : String(error) },
        error instanceof Error ? error : undefined
      );
      throw error;
    }
  }

  /**
   * Get all category names
   * @returns Array of category names
   */
  public getCategoryNames(): string[] {
    this.ensureLoaded();
    return this.categories.map(category => category.name);
  }

  /**
   * Check if a category name is valid
   * @param name The category name to check
   * @returns True if the category exists
   */
  public isValidCategoryName(name: string): boolean {
    return this.getCategoryByName(name) !== undefined;
  }

  /**
   * Get the default category (typically "Other")
   * @returns The default category, or the first category if "Other" doesn't exist
   */
  public getDefaultCategory(): Category {
    this.ensureLoaded();
    return this.getCategoryByName('Other') || this.categories[0];
  }

  /**
   * Refresh the category cache by reloading from source
   * @returns Promise that resolves when refresh is complete
   */
  public refresh(): void {
    this.logger.info('Manual cache refresh requested', 'Refresh');
    try {
      this.lastLoadTime = 0; // Force cache invalidation
      this.loadCategories();
      this.logger.info('Cache refresh completed successfully', 'Refresh', {
        categoryCount: this.categories.length,
        fallbackUsed: this.fallbackUsed
      });
    } catch (error) {
      this.logger.error(
        'Cache refresh failed',
        'Refresh',
        { error: error instanceof Error ? error.message : String(error) },
        error instanceof Error ? error : undefined
      );
      throw error;
    }
  }

  /**
   * Get cache information
   * @returns Object with cache status information
   */
  public getCacheInfo() {
    const currentTime = Date.now();
    const cacheAge = currentTime - this.lastLoadTime;
    const timeSinceLastAccess = currentTime - this.lastAccessTime;
    
    return {
      isLoaded: this.isLoaded,
      lastLoadTime: this.lastLoadTime,
      lastAccessTime: this.lastAccessTime,
      cacheAge,
      timeSinceLastAccess,
      cacheTTL: this.cacheTTL,
      isCacheValid: this.isLoaded && cacheAge < this.cacheTTL,
      cacheExpiresIn: this.isLoaded ? Math.max(0, this.cacheTTL - cacheAge) : 0,
      categoryCount: this.categories.length,
      loadCount: this.loadCount,
      totalRequests: this.cacheStats.totalRequests,
      cacheHitRatio: this.cacheStats.totalRequests > 0 ? this.cacheStats.cacheHits / this.cacheStats.totalRequests : 0
    };
  }

  /**
   * Validate external category data without loading it
   * @param categoriesData Raw category data to validate
   * @returns Detailed validation result
   */
  public validateCategoriesData(categoriesData: any): CategoryValidationResult {
    try {
      const parsedCategories = this.parseCategories(categoriesData);
      return this.validateCategories(parsedCategories);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown parsing error';
      return {
        isValid: false,
        errors: [errorMessage],
        warnings: [],
        fieldErrors: { 'parse': [errorMessage] },
        businessRuleViolations: [],
        summary: {
          totalCategories: Array.isArray(categoriesData) ? categoriesData.length : 0,
          validCategories: 0,
          errorCount: 1,
          warningCount: 0
        }
      };
    }
  }

  /**
   * Check if required fields are present in a category
   * @param category Category object to check
   * @returns True if all required fields are present and valid
   */
  public hasRequiredFields(category: any): boolean {
    return typeof category === 'object' && 
           category !== null &&
           typeof category.id === 'number' && 
           typeof category.name === 'string' &&
           category.id > 0 &&
           category.name.trim().length > 0;
  }

  /**
   * Perform type checking on category properties
   * @param category Category object to type check
   * @returns Object with type validation results for each property
   */
  public performTypeChecking(category: any): { [property: string]: { isValid: boolean; expectedType: string; actualType: string } } {
    const results: { [property: string]: { isValid: boolean; expectedType: string; actualType: string } } = {};

    // Check id
    results.id = {
      isValid: typeof category?.id === 'number',
      expectedType: 'number',
      actualType: typeof category?.id
    };

    // Check name
    results.name = {
      isValid: typeof category?.name === 'string',
      expectedType: 'string',
      actualType: typeof category?.name
    };

    // Check description (optional)
    if (category?.description !== undefined) {
      results.description = {
        isValid: typeof category.description === 'string',
        expectedType: 'string',
        actualType: typeof category.description
      };
    }

    return results;
  }

  /**
   * Validate business rules for a set of categories
   * @param categories Array of categories to validate business rules
   * @returns Array of business rule violations
   */
  public validateBusinessRules(categories: Category[]): string[] {
    const violations: string[] = [];
    const seenIds = new Set<number>();
    const seenNames = new Set<string>();

    // Check for duplicates
    categories.forEach((category, index) => {
      if (seenIds.has(category.id)) {
        violations.push(`Duplicate ID ${category.id} found at index ${index}`);
      } else {
        seenIds.add(category.id);
      }

      if (seenNames.has(category.name)) {
        violations.push(`Duplicate name '${category.name}' found at index ${index}`);
      } else {
        seenNames.add(category.name);
      }
    });

    // Check for required "Other" category
    if (!categories.some(cat => cat.name === 'Other')) {
      violations.push('Missing required "Other" category for fallback');
    }

    // Check for reasonable ID range
    const ids = categories.map(cat => cat.id);
    const maxId = Math.max(...ids);
    const minId = Math.min(...ids);
    
    if (minId < 1) {
      violations.push(`Category IDs must be positive (found ${minId})`);
    }
    
    if (maxId > 1000) {
      violations.push(`Unusually high category ID detected (${maxId}) - consider using sequential IDs`);
    }

    // Check for sequential ID gaps
    const sortedIds = [...ids].sort((a, b) => a - b);
    for (let i = 1; i < sortedIds.length; i++) {
      if (sortedIds[i] - sortedIds[i-1] > 1) {
        // Allow gaps, but warn about large ones
        if (sortedIds[i] - sortedIds[i-1] > 10) {
          violations.push(`Large ID gap detected between ${sortedIds[i-1]} and ${sortedIds[i]}`);
        }
      }
    }

    return violations;
  }

  /**
   * Ensure categories are loaded, load them if not
   */
  private ensureLoaded(): void {
    if (!this.isLoaded) {
      this.loadCategories();
    }
  }

  /**
   * Get loading statistics
   * @returns Object with loading statistics
   */
  public getStats() {
    const stats = {
      totalCategories: this.categories.length,
      isLoaded: this.isLoaded,
      errorHandling: {
        errorCount: this.errorCount,
        fallbackUsed: this.fallbackUsed,
        lastError: this.lastError ? {
          message: this.lastError.message,
          name: this.lastError.name,
          timestamp: new Date().toISOString()
        } : null
      },
      performance: {
        loadCount: this.loadCount,
        averageLoadTime: this.cacheStats.averageLoadTime,
        totalLoadTime: this.cacheStats.totalLoadTime,
        cacheHits: this.cacheStats.cacheHits,
        cacheMisses: this.cacheStats.cacheMisses,
        totalRequests: this.cacheStats.totalRequests,
        cacheHitRatio: this.cacheStats.totalRequests > 0 ? this.cacheStats.cacheHits / this.cacheStats.totalRequests : 0,
        cacheEfficiency: this.cacheStats.totalRequests > 0 ? (this.cacheStats.cacheHits / this.cacheStats.totalRequests * 100).toFixed(2) + '%' : '0%'
      },
      cacheInfo: this.getCacheInfo()
    };

    this.logger.debug('Statistics requested', 'GetStats', stats);
    return stats;
  }

  /**
   * Get the logger instance for external access
   * @returns The CategoryLogger instance
   */
  public getLogger(): CategoryLogger {
    return this.logger;
  }

  /**
   * Get diagnostic information about the CategoryLoader state
   * @returns Comprehensive diagnostic data
   */
  public getDiagnostics() {
    const diagnostics = {
      health: {
        isHealthy: this.isLoaded && !this.fallbackUsed && this.errorCount === 0,
        status: this.isLoaded ? (this.fallbackUsed ? 'fallback' : 'healthy') : 'unloaded',
        issues: [] as string[]
      },
      data: {
        categoriesLoaded: this.categories.length,
        expectedCategories: 15, // Expected from categories.json
        fallbackUsed: this.fallbackUsed,
        fallbackCategories: FALLBACK_CATEGORIES.length
      },
      errors: {
        totalErrors: this.errorCount,
        lastError: this.lastError ? {
          message: this.lastError.message,
          name: this.lastError.name,
          context: (this.lastError as CategoryLoaderError).context
        } : null
      },
      logging: {
        logLevel: LogLevel[this.logger.getLogLevel()],
        totalLogs: this.logger.getLogs().length,
        errorLogs: this.logger.getLogs(LogLevel.ERROR).length,
        recentLogs: this.logger.getLogs(undefined, 5)
      },
      timestamp: new Date().toISOString()
    };

    // Analyze health issues
    if (!this.isLoaded) {
      diagnostics.health.issues.push('Categories not loaded');
    }
    if (this.fallbackUsed) {
      diagnostics.health.issues.push('Using fallback categories due to load failure');
    }
    if (this.errorCount > 0) {
      diagnostics.health.issues.push(`${this.errorCount} error(s) encountered`);
    }
    if (this.categories.length !== diagnostics.data.expectedCategories && !this.fallbackUsed) {
      diagnostics.health.issues.push(`Unexpected category count: ${this.categories.length}, expected: ${diagnostics.data.expectedCategories}`);
    }

    this.logger.debug('Diagnostics requested', 'GetDiagnostics', {
      health: diagnostics.health.status,
      issueCount: diagnostics.health.issues.length
    });

    return diagnostics;
  }

  /**
   * Attempt to recover from error states
   * @param strategy The recovery strategy to use
   * @returns Success status of recovery attempt
   */
  public attemptRecovery(strategy: RecoveryStrategy = RecoveryStrategy.RETRY): boolean {
    this.logger.info(`Attempting recovery with strategy: ${strategy}`, 'Recovery');

    try {
      switch (strategy) {
        case RecoveryStrategy.RETRY:
          this.logger.debug('Clearing error state and retrying load', 'Recovery');
          this.errorCount = 0;
          this.lastError = null;
          this.lastLoadTime = 0; // Force reload
          this.loadCategories();
          break;

        case RecoveryStrategy.FALLBACK:
          this.logger.debug('Forcing fallback categories', 'Recovery');
          this.categories = [...FALLBACK_CATEGORIES];
          this.isLoaded = true;
          this.fallbackUsed = true;
          this.lastLoadTime = Date.now();
          break;

        case RecoveryStrategy.FAIL:
          this.logger.error('Recovery strategy set to FAIL', 'Recovery');
          throw new CategoryFallbackError('Recovery disabled by FAIL strategy', 'Recovery');

        default:
          throw new CategoryFallbackError(`Unknown recovery strategy: ${strategy}`, 'Recovery');
      }

      this.logger.info(
        `Recovery successful with strategy: ${strategy}`,
        'Recovery',
        {
          categoryCount: this.categories.length,
          fallbackUsed: this.fallbackUsed,
          errorCount: this.errorCount
        }
      );

      return true;
    } catch (error) {
      this.logger.error(
        `Recovery failed with strategy: ${strategy}`,
        'Recovery',
        { error: error instanceof Error ? error.message : String(error) },
        error instanceof Error ? error : undefined
      );

      this.errorCount++;
      this.lastError = error instanceof Error ? error : new Error(String(error));
      return false;
    }
  }

  /**
   * Reset all error tracking and statistics
   */
  public resetErrorState(): void {
    this.logger.info('Resetting error state and statistics', 'ResetErrorState');
    
    this.errorCount = 0;
    this.lastError = null;
    this.fallbackUsed = false;
    this.cacheStats = {
      totalRequests: 0,
      cacheHits: 0,
      cacheMisses: 0,
      totalLoadTime: 0,
      averageLoadTime: 0
    };
    this.loadCount = 0;
    
    this.logger.clearLogs();
    this.logger.info('Error state and statistics reset completed', 'ResetErrorState');
  }

  /**
   * Validate the current state and configuration
   * @returns Validation result with any issues found
   */
  public validateState(): { isValid: boolean; issues: string[]; recommendations: string[] } {
    const issues: string[] = [];
    const recommendations: string[] = [];

    this.logger.debug('Validating CategoryLoader state', 'ValidateState');

    // Check basic state
    if (!this.isLoaded) {
      issues.push('Categories are not loaded');
      recommendations.push('Call refresh() to load categories');
    }

    if (this.fallbackUsed) {
      issues.push('Using fallback categories instead of primary source');
      recommendations.push('Check primary data source and attempt recovery');
    }

    if (this.errorCount > 0) {
      issues.push(`${this.errorCount} error(s) have occurred`);
      recommendations.push('Review error logs and attempt recovery if needed');
    }

    // Check data integrity
    if (this.categories.length === 0) {
      issues.push('No categories available');
      recommendations.push('Verify data source and reload categories');
    }

    if (!this.fallbackUsed && this.categories.length < 10) {
      issues.push('Unexpectedly low category count');
      recommendations.push('Verify data source integrity');
    }

    // Check cache health
    const cacheInfo = this.getCacheInfo();
    if (cacheInfo.totalRequests > 0 && cacheInfo.cacheHitRatio < 0.5) {
      issues.push('Low cache hit ratio indicates potential performance issues');
      recommendations.push('Review cache TTL settings and access patterns');
    }

    const result = {
      isValid: issues.length === 0,
      issues,
      recommendations
    };

    this.logger.debug('State validation completed', 'ValidateState', {
      isValid: result.isValid,
      issueCount: issues.length,
      recommendationCount: recommendations.length
    });

    return result;
  }
} 